import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { inviteService } from '../../services/api';

/**
 * Página para visualizar e responder convites pendentes.
 */
const InvitesPage = () => {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvites();
  }, []);

  const loadInvites = async () => {
    try {
      const data = await inviteService.getMyInvites();
      setInvites(data);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar convites');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (inviteId) => {
    setActionLoading(inviteId);
    setError('');
    try {
      await inviteService.accept(inviteId);
      // Remove o convite da lista e redireciona para o grupo
      const acceptedInvite = invites.find(i => i.id === inviteId);
      setInvites(invites.filter(i => i.id !== inviteId));
      if (acceptedInvite) {
        navigate(`/groups/${acceptedInvite.groupId}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao aceitar convite');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (inviteId) => {
    setActionLoading(inviteId);
    setError('');
    try {
      await inviteService.decline(inviteId);
      setInvites(invites.filter(i => i.id !== inviteId));
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao recusar convite');
    } finally {
      setActionLoading(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando convites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="fa-duotone fa-envelope-open-text page-title-icon"></i>
            Meus Convites
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
            {invites.length > 0 
              ? `Você tem ${invites.length} convite${invites.length > 1 ? 's' : ''} pendente${invites.length > 1 ? 's' : ''}`
              : 'Nenhum convite pendente'
            }
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 24 }}>
          <i className="fa-solid fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Lista de convites */}
      {invites.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <i className="fa-duotone fa-envelope-open"></i>
          </div>
          <h3 className="empty-state-title">Sem convites pendentes</h3>
          <p className="empty-state-text">
            Quando alguém te convidar para um grupo, você verá aqui.
          </p>
          <Link to="/groups" className="btn btn-primary">
            <i className="fa-solid fa-users"></i>
            Ver Meus Grupos
          </Link>
        </div>
      ) : (
        <div>
          {invites.map((invite, index) => (
            <div 
              key={invite.id} 
              className="card animate-fadeIn"
              style={{ 
                marginBottom: 16,
                animationDelay: `${index * 0.05}s`,
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 16,
                flexWrap: 'wrap',
              }}>
                {/* Avatar do grupo */}
                <div 
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: 'var(--primary-gradient)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 20,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(invite.groupName)}
                </div>

                {/* Informações do convite */}
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: 18 }}>
                    {invite.groupName}
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    color: 'var(--text-secondary)', 
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    <i className="fa-solid fa-user"></i>
                    Convite de <strong style={{ marginLeft: 4 }}>{invite.invitedByName}</strong>
                  </p>
                  
                  {invite.message && (
                    <div style={{ 
                      marginTop: 12,
                      padding: 12,
                      background: 'var(--bg-secondary)',
                      borderRadius: 8,
                      fontSize: 14,
                      color: 'var(--text-secondary)',
                      fontStyle: 'italic',
                    }}>
                      "{invite.message}"
                    </div>
                  )}

                  <p style={{ 
                    margin: '12px 0 0 0', 
                    color: 'var(--text-muted)', 
                    fontSize: 12,
                  }}>
                    <i className="fa-solid fa-clock"></i> {formatDate(invite.createdAt)}
                  </p>
                </div>

                {/* Botões de ação */}
                <div style={{ 
                  display: 'flex', 
                  gap: 8,
                  flexShrink: 0,
                }}>
                  <button
                    onClick={() => handleDecline(invite.id)}
                    className="btn btn-outline-danger"
                    disabled={actionLoading === invite.id}
                    style={{ padding: '10px 20px' }}
                  >
                    {actionLoading === invite.id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <>
                        <i className="fa-solid fa-times"></i> Recusar
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleAccept(invite.id)}
                    className="btn btn-success"
                    disabled={actionLoading === invite.id}
                    style={{ padding: '10px 20px' }}
                  >
                    {actionLoading === invite.id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <>
                        <i className="fa-solid fa-check"></i> Aceitar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvitesPage;
